import React, { useEffect, useRef } from "react";
import type { TFunction } from "i18next";
import { useRecoilValue } from "recoil";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ForwardIcon } from "../../assets/iconComponents/Forward";
import { StyledButton } from "../shared/Button/Button.styles";
import { GetTypographyContent } from "../shared/Typography/Typography";
import {
  DetailedResultsFetalSexWrapper,
  DetailedResultsFetalSexButton,
  DetailedResultsFetalSexButtonWrappers,
  DetailedResultsButtonTextWrapper,
  DetailedResulFetalSexIconRow,
  DetailedResultsFetalSexTitle,
} from "./DetailedResults.styles";
import { DetailedResultsFetalSexReveal } from "./DetailedResultsFetalSexReveal";
import { EyeIcon } from "../../assets/iconComponents/EyeIcon";
import { getSpecificFetalSex, getUserAuth } from "../../recoil/selectors";
import { TestResultsIcon } from "../TestResults/TestResults.styles";
import { NotOrderedIcon } from "../../assets/iconComponents/NotOrdered";
import { ProcessingIcon } from "../../assets/iconComponents/Processing";
import { capitalizeFirstLetter, clearHash } from "../../utils/helpers";
import { themePalette } from "../../theme";
import { TestResultsLearnMore } from "../TestResults/TestResultsLearnMore";
import { HELP_PAGE_URL } from "../../utils/urlConstants";
import { NoValueProvided } from "../../recoil/types";
import { TrackAnalyticsEvent } from "../../services/Analytics";
import { AnalyticsEventNames } from "../../utils/analyticsEvents";

const unavailableFetalSexTranslationKeys = {
  "NO RESULT": "results.resultStatus.noResult",
  "NO CALL": "results.resultStatus.noCall",
  "NOT REPORTED": "results.resultStatus.notReported",
  "NOT ORDERED": "results.resultStatus.notOrdered",
  "N/A": "results.resultStatus.notAvailable",
} as const;

const getNoFetalDisplay = (
  t: TFunction<"translation">,
  fetalSexStatus?: string,
  fetalSexValue?: string,
) => {
  if (fetalSexValue === NoValueProvided || fetalSexStatus === "Ordered") {
    return (
      <DetailedResulFetalSexIconRow>
        <EyeIcon color={themePalette.resultsNegativeGreen} />
        {GetTypographyContent({
          content: t("results.resultStatus.seeReport"),
          size: "h2",
          textColor: "greyMedium",
        })}
      </DetailedResulFetalSexIconRow>
    );
  }
  if (
    fetalSexStatus === "Not Ordered" ||
    !fetalSexStatus ||
    !fetalSexValue ||
    fetalSexValue === "NO RESULT" ||
    fetalSexValue === "NO CALL" ||
    fetalSexValue === "NOT REPORTED" ||
    fetalSexValue === "NOT ORDERED" ||
    fetalSexValue === "N/A"
  ) {
    const unavailableFetalSexTranslationKey =
      unavailableFetalSexTranslationKeys[
        (fetalSexValue ||
          "NO RESULT") as keyof typeof unavailableFetalSexTranslationKeys
      ];
    let unavailableFetalSexDisplay = capitalizeFirstLetter(
      fetalSexValue || "NO RESULT",
    );
    if (fetalSexStatus === "Not Ordered") {
      unavailableFetalSexDisplay = t("results.resultStatus.optedOut");
    } else if (unavailableFetalSexTranslationKey) {
      unavailableFetalSexDisplay = t(unavailableFetalSexTranslationKey);
    }
    return (
      <DetailedResulFetalSexIconRow>
        <TestResultsIcon>
          <NotOrderedIcon />
        </TestResultsIcon>
        {GetTypographyContent({
          content: unavailableFetalSexDisplay,
          size: "h2",
          textColor: "greyMedium",
        })}
      </DetailedResulFetalSexIconRow>
    );
  }

  if (fetalSexStatus === "Processing") {
    return (
      <DetailedResulFetalSexIconRow>
        <TestResultsIcon>
          <ProcessingIcon />
        </TestResultsIcon>
        {GetTypographyContent({
          content: t("results.resultStatus.processing"),
          size: "h2",
          textColor: "greyMedium",
        })}
      </DetailedResulFetalSexIconRow>
    );
  }
  if (fetalSexStatus === "N/A") {
    return (
      <DetailedResulFetalSexIconRow>
        <TestResultsIcon>
          <NotOrderedIcon />
        </TestResultsIcon>
        {GetTypographyContent({
          content: "N/A",
          size: "h2",
          textColor: "greyMedium",
        })}
      </DetailedResulFetalSexIconRow>
    );
  }
};

export const DetailedResultsFetalSex: React.FC<{
  setShowFetalSexModal: (show: "" | "reveal" | "report") => void;
  showFetalSex: boolean;
  setForwardFetalSex: (forward: boolean) => void;
  requisitionID: string;
}> = ({
  setShowFetalSexModal: showFetalSexModal,
  showFetalSex,
  setForwardFetalSex,
  requisitionID,
}) => {
  const { t } = useTranslation();
  const auth = useRecoilValue(getUserAuth);
  const fetalSex = useRecoilValue(getSpecificFetalSex(requisitionID));
  const noResult = getNoFetalDisplay(t, fetalSex?.status, fetalSex?.value);
  const fetalSexRevealRef = useRef<HTMLDivElement>(null);
  const { hash } = useLocation();

  useEffect(() => {
    if (showFetalSex && fetalSexRevealRef.current) {
      const position = fetalSexRevealRef.current.getBoundingClientRect();
      window.scrollTo(position.left, position.top + window.scrollY - 20);
    }
  }, [showFetalSex, fetalSexRevealRef]);

  useEffect(() => {
    if (hash.length) {
      const hashElement = document.querySelector(hash);
      if (hashElement) {
        const position = hashElement?.getBoundingClientRect();
        window.scrollTo(position.left, position.top + window.scrollY - 100);
      }
      clearHash();
    }
  }, []);

  return (
    <DetailedResultsFetalSexWrapper ref={fetalSexRevealRef}>
      <DetailedResultsFetalSexTitle id="fetal_sex_section">
        {GetTypographyContent({
          content: t("results.detail.fetalSex"),
          size: "label",
          textColor: "greyMedium",
          isBold: true,
          isCaps: true,
          padding: "0 0 10px 0",
        })}

        {TestResultsLearnMore(
          t("results.actions.learnMore"),
          `${HELP_PAGE_URL}#fetalSexAccuracy-help`,
          "0px 20px 0px 20px",
        )}
      </DetailedResultsFetalSexTitle>
      {noResult || (
        <>
          <div>
            {GetTypographyContent({
              content: t("results.detail.fetalSexDescription"),
              size: "medium",
              textColor: "greyMedium",
              padding: "0 0 20px 0",
            })}
          </div>
          {showFetalSex ? (
            <DetailedResultsFetalSexReveal requisitionID={requisitionID} />
          ) : (
            <DetailedResultsFetalSexButtonWrappers>
              <StyledButton
                size="small"
                $backgroundColor="redMedium"
                $textColor="white"
                onClick={() => {
                  showFetalSexModal("reveal");
                  if (auth?.access_token) {
                    TrackAnalyticsEvent(
                      AnalyticsEventNames.interaction,
                      auth?.access_token,
                      auth?.user_id,
                      {
                        interaction_type: "button_click",
                        button_text: "YES, REVEAL FETAL SEX",
                        element_name: "view_babypeek_results",
                        position: "BabyPeek",
                        page_name: "AneuploidyReportPage",
                      },
                    );
                  }
                }}
              >
                <DetailedResultsFetalSexButton>
                  <div>
                    <EyeIcon color={themePalette.white} />
                  </div>
                  <div data-testid="yes-reveal-fetal-sex-button">
                    <DetailedResultsButtonTextWrapper>
                      {GetTypographyContent({
                        content: t("results.actions.revealFetalSex"),
                        size: "medium",
                        textColor: "white",
                        isCaps: true,
                        padding: "0 0 0 3px",
                      })}
                    </DetailedResultsButtonTextWrapper>
                  </div>
                </DetailedResultsFetalSexButton>
              </StyledButton>
              <StyledButton
                $reverse
                size="small"
                $backgroundColor="white"
                $textColor="redMedium"
                $borderColor="redMedium"
                onClick={() => {
                  setForwardFetalSex(true);
                  if (auth?.access_token) {
                    TrackAnalyticsEvent(
                      AnalyticsEventNames.interaction,
                      auth?.access_token,
                      auth?.user_id,
                      {
                        interaction_type: "button_click",
                        button_text: "Forward Fetal Sex",
                        element_name: "forward_fetal_sex",
                        position: "Fetal Sex",
                        page_name: "AneuploidyReportPage",
                      },
                    );
                  }
                }}
              >
                <DetailedResultsFetalSexButton>
                  <div>
                    <ForwardIcon />
                  </div>
                  <DetailedResultsButtonTextWrapper>
                    {GetTypographyContent({
                      content: t("results.actions.forwardFetalSex"),
                      size: "medium",
                      textColor: "redMedium",
                      isCaps: true,
                      padding: "0 0 0 3px",
                    })}
                  </DetailedResultsButtonTextWrapper>
                </DetailedResultsFetalSexButton>
              </StyledButton>
            </DetailedResultsFetalSexButtonWrappers>
          )}
        </>
      )}
    </DetailedResultsFetalSexWrapper>
  );
};
